import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { CheckoutForm } from "@/components/cart/CheckoutForm";
import { useCart } from "@/lib/cart";
import { useCustomerAuth } from "@/lib/auth/customer-auth-context";
import { getShippingDistricts } from "@/lib/shipping-districts";
import type { PlaceOrderResult } from "@/lib/checkout";

/**
 * Tunnel de commande (VVS_MODELE_INTERFACE.md §8), en une page à sections
 * successives (§8.1). Porté depuis apps/web/app/(shop)/commande/page.tsx :
 *
 *  - Garde d'accès ("panier invité/session client requis avant même
 *    d'appeler l'API", basée sur `ctx.sessionToken`/`ctx.accessToken`
 *    lisibles côté serveur Next.js) : sans équivalent ici — le cookie panier
 *    invité httpOnly est invisible depuis ce SPA (cf. src/lib/cart.ts). On se
 *    base uniquement sur le RÉSULTAT de `useCart()` : panier vide (ou API
 *    injoignable) → redirection `/panier`, même destination finale que
 *    l'original.
 *  - Ligne indisponible bloquant le passage en caisse (§7.2) : même
 *    redirection `/panier?cartError=…` que l'original, message identique —
 *    lu par `PanierPage` (cf. son commentaire `redirectError`).
 *  - `getAuthSession()` (lecture de cookie serveur) → `useCustomerAuth()`
 *    (contexte React, src/lib/auth/customer-auth-context.tsx, déjà en place).
 *  - `getShippingDistricts()` (fetch serveur-à-serveur avec
 *    `process.env.INTERNAL_API_URL`) → version navigateur dans
 *    src/lib/shipping-districts.ts (chemin relatif `/api/v1/...`, comme le
 *    reste de ce SPA).
 */
export function CommandePage() {
  const navigate = useNavigate();
  const { data: cart, isLoading: isCartLoading } = useCart();
  const { user: customer } = useCustomerAuth();
  const { data: districts } = useQuery({
    queryKey: ["shipping", "districts"],
    queryFn: getShippingDistricts,
    // Référentiel géographique quasi statique (§8.2) : pas besoin de le
    // refetch à chaque montage de page.
    staleTime: 60 * 60 * 1000,
  });

  const cartIsEmpty = !isCartLoading && (!cart || cart.items.length === 0);
  const hasUnavailableLine = Boolean(cart?.items.some((item) => !item.stock.inStock));

  useEffect(() => {
    if (cartIsEmpty) {
      navigate("/panier", { replace: true });
      return;
    }
    if (hasUnavailableLine) {
      navigate(
        `/panier?cartError=${encodeURIComponent("Retirez les articles indisponibles avant de commander.")}`,
        { replace: true },
      );
    }
  }, [cartIsEmpty, hasUnavailableLine, navigate]);

  function handleOrderPlaced(result: PlaceOrderResult) {
    navigate("/commande/confirmation", { state: result });
  }

  if (isCartLoading || !cart || cart.items.length === 0 || hasUnavailableLine) {
    // Rendu transitoire le temps que l'effet ci-dessus redirige — évite un
    // flash du formulaire de commande sur un panier invalide.
    return (
      <div className="container-vvs py-10">
        <p className="text-sm text-muted">Chargement…</p>
      </div>
    );
  }

  return (
    <div className="container-vvs flex flex-col gap-6 py-10">
      <div>
        <p className="text-xs uppercase tracking-wide text-muted">🔒 Commande sécurisée</p>
        <h1 className="font-display text-2xl font-semibold text-ink">Finaliser ma commande</h1>
      </div>

      <CheckoutForm cart={cart} districts={districts ?? []} customer={customer} onOrderPlaced={handleOrderPlaced} />
    </div>
  );
}
